import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { MapContainer, TileLayer, Marker, useMapEvents, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Building2, Plus, Pencil, Trash2, Search, MapPin, MousePointer } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Coordenadas de Lomas Bayas
const LOMAS_BAYAS_CENTER = [-23.4167, -69.5000];

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Crear icono personalizado
const createCustomIcon = (color = '#f97316') => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${color};
        width: 28px;
        height: 28px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 2px solid white;
        box-shadow: 0 2px 5px rgba(0,0,0,0.3);
      "></div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
  });
};

// Componente para manejar clicks en el mapa
function MapClickHandler({ onLocationSelect }) {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

const AREA_COLORS = [
  { value: '#f97316', label: 'Naranja' },
  { value: '#ef4444', label: 'Rojo' },
  { value: '#3b82f6', label: 'Azul' },
  { value: '#22c55e', label: 'Verde' },
  { value: '#a855f7', label: 'Púrpura' },
  { value: '#eab308', label: 'Amarillo' },
  { value: '#06b6d4', label: 'Cyan' },
  { value: '#ec4899', label: 'Rosa' },
];

function AdminSearchDropdown({ personas, value, displayValue, onChange }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const handleSelect = (id) => {
    onChange(id);
    setSearch('');
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = personas.filter(p => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    const fullName = `${p.nombre} ${p.apellido}`.toLowerCase();
    return q.split(/\s+/).every(word => fullName.includes(word));
  }).slice(0, 30);

  return (
    <div ref={containerRef} className="relative" style={{ zIndex: open ? 10000 : 'auto' }} data-testid="area-admin-search">
      <input type="text" placeholder="Buscar persona por nombre..."
        value={open ? search : (displayValue || '')}
        onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#a05e49]/20 focus:border-[#c4836e]" />
      {value && !open && (
        <button type="button" onClick={() => { onChange(''); setSearch(''); }} className="absolute right-2 top-2 text-slate-400 hover:text-red-500 text-xs">&#10005;</button>
      )}
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-48 overflow-y-auto" style={{ zIndex: 10001 }}>
          <div onClick={() => handleSelect('')}
            className="px-3 py-2 text-sm text-slate-400 hover:bg-slate-50 cursor-pointer">Sin administrador</div>
          {filtered.map(p => (
            <div key={p.id} onClick={() => handleSelect(p.id)}
              className="px-3 py-2 text-sm hover:bg-[#a05e49]/10 hover:text-[#a05e49] border-b border-slate-50 last:border-0 cursor-pointer">
              {p.nombre} {p.apellido}
            </div>
          ))}
          {filtered.length === 0 && search && <div className="px-3 py-2 text-sm text-slate-400">Sin resultados</div>}
        </div>
      )}
    </div>
  );
}

export const Areas = () => {
  const { isAdmin, isPlanificador } = useAuth();
  const canEdit = isAdmin() || isPlanificador();
  const [areas, setAreas] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [formData, setFormData] = useState({ 
    nombre: '', 
    administrador_id: '',
    latitud: '',
    longitud: '',
    color: '#f97316'
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, itemId: null, deps: [], loadingDeps: false });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [areasRes, personasRes] = await Promise.all([
        axios.get(`${API}/areas`),
        axios.get(`${API}/personas`)
      ]);
      setAreas(areasRes.data);
      setPersonas(personasRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const filteredAreas = areas.filter(area =>
    area.nombre.toLowerCase().includes(search.toLowerCase())
  );

  const openCreateDialog = () => {
    setEditingArea(null);
    setFormData({ nombre: '', administrador_id: '', latitud: '', longitud: '', color: '#f97316' });
    setIsDialogOpen(true);
  };

  const openEditDialog = (area) => {
    setEditingArea(area);
    setFormData({ 
      nombre: area.nombre, 
      administrador_id: area.administrador_id || '',
      latitud: area.latitud || '',
      longitud: area.longitud || '',
      color: area.color || '#f97316'
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        nombre: formData.nombre,
        administrador_id: formData.administrador_id || null,
        latitud: formData.latitud ? parseFloat(formData.latitud) : null,
        longitud: formData.longitud ? parseFloat(formData.longitud) : null,
        color: formData.color || null
      };
      if (editingArea) {
        await axios.put(`${API}/areas/${editingArea.id}`, payload);
        toast.success('Área actualizada');
      } else {
        await axios.post(`${API}/areas`, payload);
        toast.success('Área creada');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    setDeleteDialog(prev => ({ ...prev, open: true, itemId: id, deps: [], loadingDeps: true }));
    try {
      const res = await axios.get(`${API}/dependencias/areas/${id}`);
      setDeleteDialog(prev => ({ ...prev, deps: res.data.dependencias, loadingDeps: false }));
    } catch { setDeleteDialog(prev => ({ ...prev, loadingDeps: false })); }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/areas/${deleteDialog.itemId}`);
      toast.success('Area eliminada');
      setDeleteDialog({ open: false, itemId: null, deps: [], loadingDeps: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
      setDeleteDialog({ open: false, itemId: null, deps: [], loadingDeps: false });
    }
  };

  const getAdminName = (adminId) => {
    const persona = personas.find(p => p.id === adminId);
    return persona ? `${persona.nombre} ${persona.apellido}` : '-';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Áreas</h1>
          <p className="text-slate-500">Gestión de áreas organizacionales</p>
        </div>
        {canEdit && (
          <Button 
            onClick={openCreateDialog}
            className="bg-teal-500 hover:bg-teal-600"
            data-testid="create-area-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Área
          </Button>
        )}
      </div>

      <Card className="border border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar área..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-areas"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Administrador</TableHead>
                <TableHead>Ubicación</TableHead>
                <TableHead>Estado</TableHead>
                {canEdit && <TableHead className="text-right">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAreas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                    No hay áreas registradas
                  </TableCell>
                </TableRow>
              ) : (
                filteredAreas.map((area) => (
                  <TableRow key={area.id} data-testid={`area-row-${area.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: area.color ? `${area.color}20` : '#fed7aa' }}
                        >
                          <Building2 className="w-4 h-4" style={{ color: area.color || '#ea580c' }} />
                        </div>
                        {area.nombre}
                      </div>
                    </TableCell>
                    <TableCell>{getAdminName(area.administrador_id)}</TableCell>
                    <TableCell>
                      {area.latitud && area.longitud ? (
                        <span className="flex items-center gap-1 text-xs text-emerald-600">
                          <MapPin className="w-3 h-3" />
                          Georeferenciada
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">Sin coordenadas</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700">
                        Activo
                      </span>
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(area)}
                            data-testid={`edit-area-${area.id}`}
                          >
                            <Pencil className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(area.id)}
                            data-testid={`delete-area-${area.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingArea ? 'Editar Área' : 'Nueva Área'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre del Área</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                required
                data-testid="area-nombre-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="administrador">Administrador del Area</Label>
              <AdminSearchDropdown
                personas={personas}
                value={formData.administrador_id}
                displayValue={formData.administrador_id ? getAdminName(formData.administrador_id) : ''}
                onChange={(id) => setFormData({ ...formData, administrador_id: id })}
              />
            </div>

            {/* Coordenadas para el mapa */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-teal-500" />
                  <Label className="text-sm font-medium">Geolocalización (para el mapa)</Label>
                </div>
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <MousePointer className="w-3 h-3" />
                  Haz clic en el mapa para seleccionar
                </span>
              </div>
              
              {/* Mini mapa para seleccionar ubicación */}
              <div className="h-48 rounded-lg overflow-hidden border border-slate-200 mb-3">
                <MapContainer
                  center={
                    formData.latitud && formData.longitud 
                      ? [parseFloat(formData.latitud), parseFloat(formData.longitud)]
                      : LOMAS_BAYAS_CENTER
                  }
                  zoom={13}
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom={true}
                >
                  <LayersControl position="topright">
                    <LayersControl.BaseLayer name="Mapa">
                      <TileLayer
                        attribution='&copy; OpenStreetMap'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                    </LayersControl.BaseLayer>
                    <LayersControl.BaseLayer checked name="Satélite">
                      <TileLayer
                        attribution='&copy; Esri'
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                      />
                    </LayersControl.BaseLayer>
                  </LayersControl>
                  <MapClickHandler 
                    onLocationSelect={(lat, lng) => {
                      setFormData({ 
                        ...formData, 
                        latitud: lat.toFixed(6), 
                        longitud: lng.toFixed(6) 
                      });
                    }} 
                  />
                  {formData.latitud && formData.longitud && (
                    <Marker 
                      position={[parseFloat(formData.latitud), parseFloat(formData.longitud)]}
                      icon={createCustomIcon(formData.color || '#f97316')}
                    />
                  )}
                </MapContainer>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="latitud">Latitud</Label>
                  <Input
                    id="latitud"
                    type="number"
                    step="any"
                    placeholder="-23.4167"
                    value={formData.latitud}
                    onChange={(e) => setFormData({ ...formData, latitud: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="longitud">Longitud</Label>
                  <Input
                    id="longitud"
                    type="number"
                    step="any"
                    placeholder="-69.5000"
                    value={formData.longitud}
                    onChange={(e) => setFormData({ ...formData, longitud: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2 mt-3">
                <Label>Color del marcador</Label>
                <div className="flex flex-wrap gap-2">
                  {AREA_COLORS.map(c => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, color: c.value })}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        formData.color === c.value ? 'border-slate-900 scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: c.value }}
                      title={c.label}
                    />
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-teal-500 hover:bg-teal-600" data-testid="save-area-btn">
                {editingArea ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={deleteDialog.open}
        onOpenChange={(v) => !v && setDeleteDialog({ open: false, itemId: null, deps: [], loadingDeps: false })}
        onConfirm={confirmDelete}
        title="Eliminar Area"
        description="¿Esta seguro de eliminar esta area? Esta accion no se puede deshacer."
        dependencies={deleteDialog.deps}
        loadingDeps={deleteDialog.loadingDeps}
      />
    </div>
  );
};